// Books Reconciliation (V-18): the Application API's routes for LAMP and a Tally day book compared voucher by voucher,
// by period, each comparison a version of the period's sheet (ADR-0035). Amounts are paise, debits positive.
import { call, type Fetch } from "../http.js";

export type ReconciledStatus = "matched" | "differs" | "only_in_lamp" | "only_in_tally" | "twice";
export const RECONCILED_STATUSES: readonly ReconciledStatus[] = [
  "matched",
  "differs",
  "only_in_lamp",
  "only_in_tally",
  "twice",
];

export interface ReconciledPeriod {
  readonly from: string;
  readonly to: string;
}

/** One side of a line: the bill as LAMP posted it, or the voucher as Tally holds it. */
export interface BooksSide {
  readonly date: string;
  readonly number: string;
  readonly party?: string;
  readonly type?: string;
  readonly amount: number;
  readonly ledgers: readonly {
    readonly ledger: string;
    readonly amount: number;
    readonly costCentres?: Readonly<Record<string, number>>;
  }[];
}

export interface ReconciledLine {
  readonly status: ReconciledStatus;
  readonly differences: readonly ("amount" | "ledger" | "cost_centre")[];
  readonly document?: { readonly object: string; readonly id: string; readonly number: string | null };
  /** The bill the line opens to. */
  readonly href?: string;
  readonly lamp?: BooksSide;
  readonly tally?: BooksSide;
}

export interface ReconciliationSummary {
  readonly version: number;
  readonly name: string;
  readonly period: ReconciledPeriod;
  readonly dayBook: { readonly number: number; readonly name: string };
  readonly preparedAt: string;
  readonly totals: Readonly<Record<ReconciledStatus, { readonly count: number; readonly amount: number }>>;
  readonly unread: readonly string[];
  /** Where the sheet downloads from. */
  readonly sheet: string;
}

export interface Reconciliation extends ReconciliationSummary {
  readonly lines: readonly ReconciledLine[];
}

/** A Document a line opens to: its fields, as the person's Roles show them. */
export interface ReconciledDocument {
  readonly object: string;
  readonly id: string;
  readonly fields: Readonly<Record<string, unknown>>;
}

export interface BooksReconciliationApi {
  /** The periods compared, the latest first, each with its versions, oldest first. */
  periods(): Promise<
    readonly { readonly period: ReconciledPeriod; readonly versions: readonly ReconciliationSummary[] }[]
  >;
  /** One version with its lines. */
  version(period: ReconciledPeriod, version: number): Promise<Reconciliation>;
  /** Compares the period again with the latest day book that covers it: a new version. */
  again(period: ReconciledPeriod): Promise<Reconciliation>;
  /** The bill a line opens to. */
  document(href: string): Promise<ReconciledDocument>;
}

const key = (period: ReconciledPeriod) => `${period.from}_${period.to}`;

export function createBooksReconciliationApi(fetcher: Fetch): BooksReconciliationApi {
  return {
    periods: async () =>
      (
        await call<{
          periods: { period: ReconciledPeriod; versions: ReconciliationSummary[] }[];
        }>(fetcher, "/api/books-reconciliation")
      ).periods,
    version: (period, version) => call<Reconciliation>(fetcher, `/api/books-reconciliation/${key(period)}/${version}`),
    again: (period) => call<Reconciliation>(fetcher, "/api/books-reconciliation", { json: { period: key(period) } }),
    document: (href) => call<ReconciledDocument>(fetcher, href),
  };
}
